import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Flag from 'react-flagpack';
import 'react-flagpack/dist/style.css';

import Screen from '../../components/common/Screen';
import NavBar from '../../components/common/NavBar';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import { apiRequest, awardArt, teamPhotoOf } from '../../utils';
import '../user/PublicProfile.css';
import './PublicCompany.css';

const userFallback = require('./../../assets/images/fallback/user.png');
const companyFallback = require('./../../assets/images/fallback/company.png');

/* A prize's picture is decided in ONE place for every screen that draws one (`awardArt` in
   utils): what was uploaded with the award, else our bundled art by name, else the cup. */

const yearOf = (date) => {
  if (!date) return null;
  const d = new Date(date);
  return Number.isFinite(d.getFullYear()) ? d.getFullYear() : null;
};

// NavBar self-hides until the viewer is logged in (see NavBar.js), so it's
// safe to render here unconditionally. The Back arrow is for a logged-in visitor only (they
// came here from inside the app — a list, the home, a chip); history first, /home on a cold open.
const Layout = ({ children, authed }) => (
  <Screen>
    {authed && <Back to="/home" />}
    <div className="top">{children}</div>
    <div className="bottom">
      <NavBar />
    </div>
  </Screen>
);

const Page = () => {
  const { t } = useTranslation(['common', 'awards']);
  const navigate = useNavigate();
  const { companyId } = useParams();

  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'notfound'

  useEffect(() => {
    if (!companyId) { setStatus('notfound'); return; }
    let cancelled = false;
    setStatus('loading');

    apiRequest('GET', `/public-company/${companyId}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setStatus('ok');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('notfound');
      });

    return () => { cancelled = true; };
  }, [companyId]);

  const heroPhoto = useMemo(() => data?.photo_url || companyFallback, [data]);
  const heroBg = useMemo(() => data?.cover_image || data?.photo_url || companyFallback, [data]);
  // Derived from the same response — null while loading, true/false after.
  const authed = data && typeof data.is_authenticated === 'boolean' ? data.is_authenticated : null;

  if (status === 'loading') {
    return (
      <Layout authed={authed}>
        <div className="pp-page">
          <div className="pp-skeleton pp-skel-hero" />
          <div className="pp-skeleton pp-skel-stats" />
          <div className="pp-skeleton pp-skel-row" />
          <div className="pp-skeleton pp-skel-row" />
        </div>
      </Layout>
    );
  }

  if (status === 'notfound' || !data) {
    return (
      <Layout authed={authed}>
        <div className="pp-page">
          <div className="pp-empty">
            <div className="pp-empty-icon"><Icon name="info" /></div>
            <div className="pp-empty-title">{t('companyNotFound', { defaultValue: 'Company not found' })}</div>
            <p className="pp-empty-sub">{t('companyNotFoundDesc', { defaultValue: 'This company does not exist or is no longer available.' })}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const { name, bio, country, website, is_community, stats, current_teams, events, awards, top_players, rivals, is_owner } = data;
  // A company's teams fall back to its logo, as on the team page; a community's teams are not its.
  const teamLogo = is_community ? null : data.photo_url;

  const handleOwnerAction = () => {
    sessionStorage.setItem('otherCompanyId', companyId);
  };

  return (
    <Layout authed={authed}>
      <div className="pp-page">

        {/* HERO */}
        <section className="pp-hero">
          <div className="pp-hero-bg" style={{ backgroundImage: `url(${heroBg})` }} />
          <div className="pp-hero-inner">
            <div className="pp-avatar-ring">
              <img src={heroPhoto} alt="" className="pp-avatar pc-avatar" />
            </div>

            <h1 className="pp-name">
              {name}
              {country && (
                <span className="pp-flag-wrap">
                  <Flag code={country} size="m" hasBorderRadius hasBorder={false} hasDropShadow={false} />
                </span>
              )}
            </h1>

            {is_community && (
              <span className="pc-community-badge">
                <Icon name="users-alt" /> {t('community')}
              </span>
            )}

            {bio && <p className="pp-bio">{bio}</p>}

            {website && (
              <a className="pc-website" href={website.startsWith('http') ? website : `https://${website}`} target="_blank" rel="noopener noreferrer">
                <Icon name="globe" /> {website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </a>
            )}
          </div>
        </section>

        {/* OWNER ACTIONS — contact person of this company */}
        {is_owner && (
          <div className="pc-owner-actions">
            <Link to="/edit-company" onClick={handleOwnerAction} className="pc-owner-btn">
              <Icon name="settings" /> {is_community ? t('editCommunity', { defaultValue: 'Edit community' }) : t('editCompany', { defaultValue: 'Edit company' })}
            </Link>
            <Link to="/manage-employees" onClick={handleOwnerAction} className="pc-owner-btn">
              <Icon name="users-gear" /> {is_community ? t('manageMembers') : t('manageEmployees')}
            </Link>
            <Link to="/company-readiness" onClick={handleOwnerAction} className="pc-owner-btn">
              <Icon name="list-check" /> {t('viewReadiness')}
            </Link>
          </div>
        )}

        {/* STATS (4-up) */}
        {stats && (
          <div className="pp-stats pc-stats">
            <div className="pp-stat">
              <div className="pp-stat-value">{stats.event_count || 0}</div>
              <div className="pp-stat-label">{t('events', { defaultValue: 'Events' })}</div>
            </div>
            <div className="pp-stat">
              <div className="pp-stat-value">{stats.team_count || 0}</div>
              <div className="pp-stat-label">{t('teams')}</div>
            </div>
            <div className="pp-stat">
              <div className="pp-stat-value">{stats.player_count || 0}</div>
              <div className="pp-stat-label">{t('players')}</div>
            </div>
            <div className="pp-stat">
              <div className="pp-stat-value">{stats.award_count || 0}</div>
              <div className="pp-stat-label">{t('awards')}</div>
            </div>
          </div>
        )}

        {/* CURRENT TEAMS */}
        {current_teams?.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('currentlyCompeting', { defaultValue: 'Currently competing' })}</h2>
              <span className="pp-section-count">{current_teams.length}</span>
            </header>
            <div className="pc-current-grid">
              {current_teams.map((tm) => (
                <Link to={`/public-team/${tm.id}`} key={tm.id} className="pp-current">
                  <img src={teamPhotoOf(tm, tm.photo_url, teamLogo)} alt="" className="pp-current-img" />
                  <div className="pp-current-meta">
                    <p className="pp-current-team">{tm.name}</p>
                    <p className="pp-current-event">{tm.event_name}</p>
                  </div>
                  <Icon name="angle-right" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* AWARDS */}
        {awards?.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('awards')}</h2>
              <span className="pp-section-count">{awards.length}</span>
            </header>
            <div className="pp-awards-grid">
              {awards.map((aw) => (
                <Link key={aw.id} to={`/public-team/${aw.team_id}`} className="pp-award">
                  <div className="pp-award-img-wrap">
                    <img src={awardArt(aw.award_name, aw.image)} alt="" className="pp-award-img" />
                  </div>
                  <div className="pp-award-name">
                    {t(aw.award_name, { ns: 'awards', defaultValue: aw.award_name.replace(/(?!^)([A-Z])/g, ' $1') })}
                  </div>
                  {yearOf(aw.event_date) && <div className="pp-award-event">{yearOf(aw.event_date)}</div>}
                </Link>
              ))}
            </div>
            {authed && (
              <Link className="pc-more-link" to="/company-award-lb">
                {t('viewAwardLeaderboard', { defaultValue: 'View company award leaderboard' })}
                <Icon name="angle-right" />
              </Link>
            )}
          </section>
        )}

        {/* TOP PLAYERS */}
        {top_players?.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('topPlayers', { defaultValue: 'Top players' })}</h2>
              <span className="pp-section-count">{top_players.length}</span>
            </header>
            <div className="pp-buddy-grid">
              {top_players.map((p) => {
                const reachable = p.is_public || authed;
                const Wrapper = reachable ? Link : 'div';
                const wrapperProps = reachable ? { to: `/u/${p.id}` } : {};
                const detail = p.awards > 0
                  ? `${p.awards} ${p.awards === 1 ? t('award') : t('awards')}`
                  : `${p.teams} ${p.teams === 1 ? t('team') : t('teams')}`;
                return (
                  <Wrapper key={p.id} className={`pp-buddy ${reachable ? '' : 'pp-buddy-locked'}`} {...wrapperProps}>
                    <img src={p.photo_url || userFallback} alt="" className="pp-buddy-img" />
                    <div className="pp-buddy-name">{p.full_name}</div>
                    <div className="pp-buddy-shared">{detail}</div>
                  </Wrapper>
                );
              })}
            </div>
          </section>
        )}

        {/* EVENTS */}
        {events?.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('events', { defaultValue: 'Events' })}</h2>
              <span className="pp-section-count">{events.length}</span>
            </header>

            {events.map((ev) => (
              <div key={ev.id} className="pc-event-card">
                <div className="pc-event-head">
                  {ev.id ? (
                    <Link to={`/public-event/${ev.id}`} className="pc-event-name">
                      {ev.name}
                    </Link>
                  ) : (
                    <h3 className="pc-event-name">{ev.name}</h3>
                  )}
                  <span className="pc-event-meta">
                    {yearOf(ev.date) && <>{yearOf(ev.date)} · </>}
                    {ev.teams.length} {ev.teams.length === 1 ? t('team') : t('teams')}
                  </span>
                </div>
                <div className="pc-event-teams">
                  {ev.teams.map((tm) => (
                    <Link to={`/public-team/${tm.id}`} key={tm.id} className="pc-team-chip">
                      <img src={teamPhotoOf(tm, tm.photo_url, teamLogo)} alt="" className="pc-team-chip-img" />
                      <span className="pc-team-chip-name">{tm.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* CHALLENGED COMPANIES */}
        {rivals?.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('challengedCompanies', { defaultValue: 'Challenged companies' })}</h2>
              <span className="pp-section-count">{rivals.length}</span>
            </header>
            <div className="pc-rival-grid">
              {rivals.map((r) => (
                <Link to={`/public-company/${r.id}`} key={r.id} className="pc-rival">
                  <div className="pc-rival-img-wrap">
                    {r.photo_url
                      ? <img src={r.photo_url} alt="" className="pc-rival-img" />
                      : <span className="pc-rival-fallback">{(r.name || '?').charAt(0).toUpperCase()}</span>}
                  </div>
                  <div className="pc-rival-name">{r.name}</div>
                  <div className="pc-rival-count">
                    {r.challenge_count} {r.challenge_count === 1 ? t('challenge') : t('challenges')}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

      </div>
    </Layout>
  );
};

export default Page;
